import Radio from 'client/components/GDS/Radio/Radio';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

/**
 * WorkpackExportOptions component with radio buttons for Paper Orientation, Paper size, and Map scale.
 * The component extracts orientation and size from the layout name (e.g., "A4 Landscape" -> orientation: "Landscape", size: "A4")
 */
const WorkpackExportOptions = ({ orientation, onOrientationChange, scale, onScaleChange, capabilities, ...props }) => {
    const intl = useIntl();

    // Extract unique orientations and sizes from layout names
    const orientations = new Set();
    const sizes = new Set();

    capabilities.layouts.forEach(layout => {
        // Parse layout name (e.g., "A4 Landscape" -> ["A4", "Landscape"])
        const parts = layout.name.split(' ');
        if (parts.length >= 2) {
            sizes.add(parts[0]); // e.g., "A4", "A3"
            orientations.add(parts[1]); // e.g., "Landscape", "Portrait"
        }
    });

    // Get current orientation and size from selected layout
    const orientationList = Array.from(orientations);
    const sizeList = Array.from(sizes);

    const currentParts = orientation ? orientation.split(' ') : [];
    const currentSize = currentParts[0] || '';
    const currentOrientation = currentParts[1] || '';

    // Find layout matching current selections
    const currentLayout = orientation ? capabilities.layouts.find(layout => layout.name === orientation) : capabilities.layouts[0];
    const scaleOptions = currentLayout.scales;

    const handleOrientationChange = (newOrientation) => {
        // Use first available size if none selected, otherwise use current
        const targetSize = currentSize || sizeList[0];
        const newLayoutName = `${targetSize} ${newOrientation}`;
        const newLayout = capabilities.layouts.find(layout => layout.name === newLayoutName);
        if (newLayout) {
            onOrientationChange(newLayoutName);
            // Only clear scale if it's not valid for the new layout
            if (scale && !newLayout.scales.includes(parseInt(scale))) {
                onScaleChange(null);
            }
        }
    };

    const handleSizeChange = (newSize) => {
        // Use first available orientation if none selected, otherwise use current
        const targetOrientation = currentOrientation || orientationList[0];
        const newLayoutName = `${newSize} ${targetOrientation}`;
        const newLayout = capabilities.layouts.find(layout => layout.name === newLayoutName);
        if (newLayout) {
            onOrientationChange(newLayoutName);
            // Only clear scale if it's not valid for the new layout
            if (scale && !newLayout.scales.includes(parseInt(scale))) {
                onScaleChange(null);
            }
        }
    };

    const handleScaleChange = (newScale) => {
        onScaleChange(newScale);
    };

    return <div className={'govuk-grid-row'} data-testid={props['data-testid']}>
        {/* Paper Size */}
        <div className="govuk-grid-column-one-third">
            <div className="govuk-form-group">
                <fieldset className="govuk-fieldset">
                    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
                        <h3 className="govuk-fieldset__heading">
                            {intl.formatMessage({ id: 'workpackExportOptions.paperSize.label' })}
                        </h3>
                    </legend>
                    <div className="govuk-radios" data-module="govuk-radios">
                        {sizeList.map((size) => (
                            <Radio
                                key={size}
                                name="workpackSize"
                                id={`size-${size}`}
                                label={size}
                                value={size}
                                checked={currentSize === size}
                                onChange={() => handleSizeChange(size)}
                                data-testid={props['data-testid'] ? `${props['data-testid']}.size.${size}` : null}
                            />
                        ))}
                    </div>
                </fieldset>
            </div>

            {/* Map Scale - moved below Paper Size */}
            <div className="govuk-form-group">
                <fieldset className="govuk-fieldset">
                    <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
                        <h3 className="govuk-fieldset__heading">
                            {intl.formatMessage({ id: 'workpackExportOptions.mapScale.label' })}
                        </h3>
                    </legend>
                    <div className="govuk-radios" data-module="govuk-radios">
                        {scaleOptions.map((scaleOption) => (
                            <Radio
                                key={scaleOption}
                                name="workpackScale"
                                id={`scale-${scaleOption}`}
                                label={`1:${scaleOption}`}
                                value={scaleOption.toString()}
                                checked={scale === scaleOption.toString()}
                                onChange={() => handleScaleChange(scaleOption.toString())}
                                data-testid={props['data-testid'] ? `${props['data-testid']}.scale.${scaleOption}` : null}
                            />
                        ))}
                    </div>
                </fieldset>
            </div>
        </div>

        {/* Paper Orientation */}
        {orientationList.length > 1 && (
            <div className="govuk-grid-column-one-third">
                <div className="govuk-form-group">
                    <fieldset className="govuk-fieldset">
                        <legend className="govuk-fieldset__legend govuk-fieldset__legend--s">
                            <p className="govuk-fieldset__heading">
                                {intl.formatMessage({ id: 'workpackExportOptions.paperOrientation.label' })}
                            </p>
                        </legend>
                        <div className="govuk-radios" data-module="govuk-radios">
                            {orientationList.map((orient) => (
                                <Radio
                                    key={orient}
                                    name="workpackOrientation"
                                    id={`orientation-${orient}`}
                                    label={orient}
                                    value={orient}
                                    checked={currentOrientation === orient}
                                    onChange={() => handleOrientationChange(orient)}
                                    data-testid={props['data-testid'] ? `${props['data-testid']}.orientation.${orient}` : null}
                                />
                            ))}
                        </div>
                    </fieldset>
                </div>
            </div>
        )}
    </div>;
};

WorkpackExportOptions.propTypes = {
    onOrientationChange: PropTypes.func.isRequired,
    onScaleChange: PropTypes.func.isRequired,
    capabilities: PropTypes.object.isRequired,
    orientation: PropTypes.string,
    scale: PropTypes.string,
    'data-testid': PropTypes.string
};

export default WorkpackExportOptions;
